import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, CheckCircle2, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { hero, company } from "../mock";
import ZipChecker from "./ZipChecker";

const HeroCarousel = () => {
  const slides = hero.images && hero.images.length ? hero.images : [hero.image];
  const [index, setIndex] = useState(0);

  const go = useCallback(
    (dir) => setIndex((i) => (i + dir + slides.length) % slides.length),
    [slides.length]
  );

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % slides.length), 4500);
    return () => clearInterval(id);
  }, [slides.length]);

  return (
    <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white/10 bg-brand-slate aspect-square">
      {slides.map((src, i) => (
        <img
          key={i}
          src={src}
          alt={`Fab Build Pro roofing project ${i + 1}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}

      {/* controls */}
      <button
        onClick={() => go(-1)}
        aria-label="Previous slide"
        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-brand-orange text-white flex items-center justify-center backdrop-blur transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={() => go(1)}
        aria-label="Next slide"
        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-brand-orange text-white flex items-center justify-center backdrop-blur transition-colors"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-2 rounded-full transition-all ${
              i === index ? "w-6 bg-brand-orange" : "w-2 bg-white/60 hover:bg-white"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const Hero = () => {
  return (
    <section className="relative overflow-hidden bg-brand-navy">
      {/* decorative gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-slate to-brand-navy" />
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-orange/10 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
        {/* Left */}
        <div className="animate-fade-up">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse" />
            <span className="text-white/90 text-sm font-medium">
              {hero.eyebrow}
            </span>
          </div>

          <h1 className="font-display font-extrabold text-white text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.05] tracking-tight">
            {hero.titleTop}
            <br />
            <span className="text-brand-orange">{hero.titleAccent}</span>
          </h1>

          <p className="text-white/75 text-lg mt-6 max-w-xl leading-relaxed">
            {hero.subtitle}
          </p>

          <div className="flex flex-wrap gap-3 mt-8">
            <a
              href={company.phoneHref}
              className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangedark text-white font-semibold px-6 py-3.5 rounded-full shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
            >
              <Phone className="w-5 h-5" />
              Call {company.phone}
            </a>
            <Link
              to="/contact"
              onClick={() => window.gtag_report_conversion && window.gtag_report_conversion()}
              className="inline-flex items-center gap-2 bg-white/5 border border-white/25 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-white/10 transition-all"
            >
              Get Free Estimate
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-3 mt-8">
            {hero.badges.map((b) => (
              <div key={b} className="flex items-center gap-2 text-white/85 text-sm">
                <CheckCircle2 className="w-5 h-5 text-brand-orange" />
                {b}
              </div>
            ))}
          </div>

          {/* Google review rating */}
          <a
            href={company.googleReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 mt-6 bg-white/10 hover:bg-white/15 border border-white/15 rounded-full pl-2 pr-4 py-2 transition-colors"
          >
            <img
              src="https://www.gstatic.com/marketing-cms/assets/images/d5/dc/cfe9ce8b4425b410b49b7f2dd3f3/g.webp=s96-fcrop64=1,00000000ffffffff-rw"
              alt="Google"
              className="w-6 h-6"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4.5 h-4.5 w-4 h-4 fill-brand-yellow text-brand-yellow" />
              ))}
            </div>
            <span className="text-white font-semibold text-sm">
              {company.rating} · Google Reviews
            </span>
          </a>
        </div>

        {/* Right */}
        <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
          <HeroCarousel />

          {/* Floating ZIP card */}
          <div className="lg:absolute lg:-bottom-8 lg:-right-6 mt-6 lg:mt-0 lg:w-[340px]">
            <ZipChecker variant="card" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
